import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import Api from "lib/api/httpRequestHelper";

import Filters from "./components/filters";
import ModalConsultFilters from "./components/modal-consult-filters";
import SelectProductsTable from "./components/select-products-table";
import ModalComponent from "./components/modal";
import ProductsTable from "./components/products-table";

import UtilsFncs from "utils/UtilsFncs";
import { utils, writeFileXLSX } from "xlsx";

import {
  setSelectedProducts,
  updateProductFilters,
} from "redux/reducers/criarVitrineReducer";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import {
  mergeProductsByCodigo,
  selectedProductsNeedGradeEnrichment,
  enrichSelectedProductsWithListItems,
} from "../utils/wizard-helpers";

export default function IncludeProducts() {
  const [isLoading, setIsLoading] = useState(false);
  const [modalFilters, setModalFilters] = useState({
    productSearch: "",
    linhaVenda: null,
    grade: null,
  });
  const [preSelectedProducts, setPreSelectedProducts] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [isImportingSku, setIsImportingSku] = useState(false);
  const modalOpenRef = useRef(false);

  const [productsTablePagination, setProductsTablePagination] = useState({
    page: 1,
    sizePerPage: 10,
  });

  const dispatch = useDispatch();
  const snackbar = useSnackbar();

  const { selectedProducts, productFilters, formData } = useSelector(
    (state) => state.criarVitrine,
  );
  const productFiltersRef = useRef(productFilters);
  const enrichProductsAttemptRef = useRef("");

  const buildSearchBody = useCallback(
    async ({
      mergedFilters,
      page,
      pageSize,
      filterValue,
      base64FileOverride,
      retornarTodos,
    }) => {
      const eanValue = Array.isArray(mergedFilters?.sku)
        ? mergedFilters.sku.length > 0
          ? mergedFilters.sku.map((item) => item?.value).filter(Boolean)
          : null
        : mergedFilters?.sku?.value
          ? [mergedFilters.sku.value]
          : null;

      const base64File =
        base64FileOverride ??
        (mergedFilters?.importFile
          ? await UtilsFncs.FileToB64(mergedFilters?.importFile)
          : null);

      return {
        page,
        pageSize,
        idMarca: formData?.brand?.value,
        linhaVenda: mergedFilters?.linhaVenda?.label,
        grade: mergedFilters?.grade?.label,
        colecaoProduto: mergedFilters?.collection?.label,
        apenasColecaoAtiva: false,
        apenasComEstoque: true,
        ean: eanValue?.toString(),
        filter: filterValue,
        base64File,
        fileType: mergedFilters?.importFile?.name?.split(".").pop(),
        retornarTodos: Boolean(retornarTodos),
      };
    },
    [formData?.brand?.value],
  );

  const handleSearch = useCallback(
    async (overrides) => {
      const { initializePreSelection, ...restOverrides } = overrides ?? {};
      const baseFilters = productFilters;
      const mergedFilters = { ...baseFilters, ...restOverrides };
      const page =
        restOverrides?.page ??
        mergedFilters?.page ??
        Math.max(0, (currentPage ?? 1) - 1);
      const sizePerPage =
        restOverrides?.sizePerPage ??
        mergedFilters?.sizePerPage ??
        currentSizePerPage;
      const filterValue =
        restOverrides?.filter ??
        restOverrides?.productSearch ??
        mergedFilters?.productSearch ??
        null;

      try {
        setIsLoading(true);
        dispatch(
          updateProductFilters({
            ...mergedFilters,
            page,
            sizePerPage,
          }),
        );

        const response = await Api.post(
          "b2b-showcase-product-list",
          "showcaseB2B",
          await buildSearchBody({
            mergedFilters,
            page,
            pageSize: sizePerPage,
            filterValue,
          }),
          true,
        );

        const lista = response.Result?.ListaProdutos ?? [];
        setProductsData(lista);
        setTotalSize(response.Result?.TotalRegistros ?? 0);
        if (initializePreSelection) {
          setPreSelectedProducts(selectedProducts);
          setModalFilters({
            productSearch: "",
            linhaVenda: mergedFilters.linhaVenda ?? null,
            grade: mergedFilters.grade ?? null,
          });
        }

        if (lista.length > 0) {
          setOpen(true);
        } else {
          const msg = modalOpenRef.current
            ? "Nenhum produto encontrado para esta pesquisa. Ajuste o texto ou os filtros."
            : "Nenhum produto encontrado com os filtros selecionados.";
          snackbar.showMessage(msg, {
            severity: modalOpenRef.current ? "warning" : "error",
          });
          if (modalOpenRef.current) {
            setOpen(true);
          } else {
            setOpen(false);
          }
        }
      } catch (error) {
        snackbar.showMessage(
          "Não foi possível buscar os produtos. Tente novamente mais tarde.",
          { severity: "error" },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      buildSearchBody,
      currentPage,
      currentSizePerPage,
      dispatch,
      productFilters,
      snackbar,
    ],
  );

  const handleSelectAllResults = useCallback(async () => {
    const mergedFilters = {
      ...productFiltersRef.current,
      ...modalFilters,
    };
    const filterValue = modalFilters.productSearch || null;

    // Uma chamada só (retornarTodos), sem loop de páginas: a query já faz o dedup (DISTINCT ON)
    // numa única execução, em vez de repeti-lo do zero a cada página de um loop client-side.
    const response = await Api.post(
      "b2b-showcase-product-list",
      "showcaseB2B",
      await buildSearchBody({
        mergedFilters,
        page: 0,
        pageSize: 0,
        filterValue,
        retornarTodos: true,
      }),
      true,
    );

    const all = response.Result?.ListaProdutos ?? [];

    return mergeProductsByCodigo([], all);
  }, [buildSearchBody, modalFilters]);

  const handleDownloadSkuTemplate = useCallback(() => {
    const sheet = utils.aoa_to_sheet([
      ["sku"],
      ["123456"],
      ["789012"],
      ["345678"],
    ]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, sheet, "SKU");
    writeFileXLSX(wb, "modelo-importacao-sku-vitrine-b2b.xlsx");
  }, []);

  // Diferente da busca por filtros: importar um arquivo de SKU não é "pesquisar para revisar depois" —
  // os produtos encontrados pelos SKUs do arquivo já entram direto na tabela de produtos da vitrine.
  const handleImportSku = useCallback(
    async (file) => {
      if (!file) return;

      setIsImportingSku(true);
      try {
        // Uma chamada só (retornarTodos) — ver mesma observação em handleSelectAllResults.
        const body = await buildSearchBody({
          mergedFilters: { importFile: file },
          page: 0,
          pageSize: 0,
          filterValue: null,
          retornarTodos: true,
        });

        const response = await Api.post(
          "b2b-showcase-product-list",
          "showcaseB2B",
          body,
          true,
        );

        if (!response?.Success) {
          throw new Error(
            response?.ValidationMessage ||
              "Falha ao importar os produtos do arquivo.",
          );
        }

        const all = response.Result?.ListaProdutos ?? [];

        if (all.length === 0) {
          snackbar.showMessage(
            "Nenhum produto encontrado para os SKUs importados.",
            { severity: "warning" },
          );
          return;
        }

        dispatch(
          setSelectedProducts(mergeProductsByCodigo(selectedProducts, all)),
        );

        snackbar.showMessage(
          `${all.length} produto(s) importado(s) e adicionado(s) à vitrine.`,
          { severity: "success" },
        );
      } catch (error) {
        snackbar.showMessage(
          error?.message ||
            "Não foi possível importar os produtos do arquivo. Verifique o arquivo e tente novamente.",
          { severity: "error" },
        );
      } finally {
        setIsImportingSku(false);
      }
    },
    [buildSearchBody, dispatch, selectedProducts, snackbar],
  );

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleModalSearch = useCallback(() => {
    setCurrentPage(1);
    handleSearch({
      ...productFiltersRef.current,
      ...modalFilters,
      filter: modalFilters.productSearch || null,
      productSearch: modalFilters.productSearch || null,
      page: 0,
      initializePreSelection: false,
    });
  }, [handleSearch, modalFilters]);

  const handleAddProducts = () => {
    dispatch(
      setSelectedProducts(
        mergeProductsByCodigo(selectedProducts, preSelectedProducts),
      ),
    );
    closeModal();
  };

  const handlePaginationChange = (page, sizePerPage) => {
    const nextSize = sizePerPage ?? currentSizePerPage;
    const nextPage = page ?? currentPage;

    setCurrentSizePerPage(nextSize);
    setCurrentPage(nextPage);

    handleSearch({
      ...productFilters,
      page: nextPage - 1,
      sizePerPage: nextSize,
    });
  };

  useEffect(() => {
    productFiltersRef.current = productFilters;
  }, [productFilters]);

  useEffect(() => {
    modalOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!selectedProductsNeedGradeEnrichment(selectedProducts)) return;
    if (!formData?.brand?.value) return;

    const codigos = selectedProducts
      .map((p) => String(p.Codigo ?? "").trim())
      .filter(Boolean);
    if (!codigos.length) return;

    const attemptKey = codigos.slice().sort().join(",");
    if (enrichProductsAttemptRef.current === attemptKey) return;
    enrichProductsAttemptRef.current = attemptKey;

    let cancelled = false;

    async function enrichProducts() {
      try {
        const response = await Api.post(
          "b2b-showcase-product-list",
          "showcaseB2B",
          {
            page: 0,
            pageSize: Math.min(Math.max(codigos.length, 1), 1000),
            idMarca: formData.brand.value,
            codigosProduto: codigos.join(","),
          },
          true,
        );

        if (cancelled) return;

        const lista = response.Result?.ListaProdutos ?? [];
        if (!lista.length) return;

        dispatch(
          setSelectedProducts(
            enrichSelectedProductsWithListItems(selectedProducts, lista),
          ),
        );
      } catch {
        enrichProductsAttemptRef.current = "";
      }
    }

    enrichProducts();

    return () => {
      cancelled = true;
    };
  }, [dispatch, formData?.brand?.value, selectedProducts]);

  useEffect(() => {
    if (!open) return;
    setModalFilters({
      productSearch: "",
      linhaVenda: productFilters.linhaVenda ?? null,
      grade: productFilters.grade ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderColor: themeColorsDesingSystem.stone.main,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ mr: "auto" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Importar produtos por SKU
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Selecione um arquivo CSV, TXT ou XLSX com a coluna &quot;sku&quot;
            (Código do Produto) — os produtos encontrados são adicionados direto
            à vitrine.
          </Typography>
        </Box>
        <Button
          onClick={handleDownloadSkuTemplate}
          variant="text"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
        >
          Baixar modelo
        </Button>
        <Button
          component="label"
          variant="outlined"
          size="small"
          disabled={isImportingSku}
          startIcon={
            isImportingSku ? (
              <CircularProgress size={16} />
            ) : (
              <UploadFileOutlinedIcon />
            )
          }
        >
          {isImportingSku ? "Importando..." : "Importar SKU"}
          <Box
            component="input"
            type="file"
            sx={{ display: "none" }}
            accept=".csv,.xlsx,.xls,.txt"
            disabled={isImportingSku}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) handleImportSku(file);
            }}
          />
        </Button>
      </Paper>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderColor: themeColorsDesingSystem.stone.main,
        }}
      >
        <Filters
          handleSearch={handleSearch}
          searching={isLoading}
          idMarca={formData?.brand?.value}
        />
      </Paper>
      <ModalComponent
        open={open}
        onClose={closeModal}
        width="92vw"
        title="Consultar Produtos"
      >
        <ModalConsultFilters
          values={modalFilters}
          onChange={setModalFilters}
          onSearch={handleModalSearch}
          searching={isLoading}
          idMarca={formData?.brand?.value}
        />
        <Box mt={2}>
          <SelectProductsTable
            data={productsData}
            loading={isLoading}
            totalSize={totalSize}
            currentPage={currentPage}
            currentSizePerPage={currentSizePerPage}
            selectedProducts={preSelectedProducts}
            setSelectedProducts={setPreSelectedProducts}
            handlePaginationChange={handlePaginationChange}
            onSelectAllResults={handleSelectAllResults}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            mt={2}
            pt={2}
            borderTop={`1px solid ${themeColorsDesingSystem.stone.main}`}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={preSelectedProducts.length === 0}
              onClick={handleAddProducts}
              sx={{ minWidth: 160, fontWeight: 700 }}
            >
              Adicionar
            </Button>
          </Box>
        </Box>
      </ModalComponent>
      <Paper
        variant="outlined"
        sx={{
          mt: 4,
          p: selectedProducts.length > 0 ? 2 : 6,
          borderColor: themeColorsDesingSystem.stone.main,
          minHeight: 200,
        }}
      >
        {selectedProducts.length === 0 && !isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <Inventory2OutlinedIcon
              sx={{
                fontSize: 48,
                color: themeColorsDesingSystem.stone.dark,
                mb: 2,
              }}
            />
            <Typography variant="h6" color="textSecondary">
              Nenhum produto adicionado
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Utilize os filtros acima ou importe uma lista de SKUs para popular
              a vitrine.
            </Typography>
          </Box>
        ) : (
          <ProductsTable
            totalSize={selectedProducts?.length ?? 0}
            currentPage={productsTablePagination.page}
            currentSizePerPage={productsTablePagination.sizePerPage}
            setProductsTablePagination={setProductsTablePagination}
          />
        )}
      </Paper>
    </>
  );
}
